import React, { useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_NAME = 'Sales demo — fictional data and simulated results';
const REFERENCE_PATTERN = /^[A-Za-z0-9._:-]{8,100}$/;

function safeError(data, status) {
  const detail = data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail.message === 'string') return detail.message;
  return `Provisioning failed (HTTP ${status})`;
}

export default function SalesDemoProvisioningPanel({ onProvisioned }) {
  const [name, setName] = useState(DEFAULT_NAME);
  const [requestId, setRequestId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const normalisedReference = requestId.trim();
  const valid = name.trim().length >= 3 && REFERENCE_PATTERN.test(normalisedReference);

  const provision = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const response = await fetch(`${API_URL}/api/platform/sales-demo-tenants`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), request_id: normalisedReference }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(safeError(data, response.status));
      setResult(data);
      if (onProvisioned) await onProvisioned(data);
    } catch (provisionError) {
      setError(provisionError?.message || 'Provisioning failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel" style={{ marginBottom: 12 }} data-testid="sales-demo-provisioning-panel">
      <div className="panel-header">Sales demo workspaces</div>
      <div className="panel-body" style={{ display: 'grid', gap: 12 }}>
        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 13 }}>
          Create a new, isolated workspace containing fictional fixture data only. This operation
          does not create credentials or send an invitation, and live provider dispatch remains disabled.
        </p>
        <label style={{ display: 'grid', gap: 5 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Workspace name</span>
          <input
            className="input"
            data-testid="sales-demo-workspace-name"
            maxLength={80}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <label style={{ display: 'grid', gap: 5 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Provisioning reference</span>
          <input
            className="input"
            data-testid="sales-demo-provisioning-reference"
            maxLength={100}
            placeholder="sales-demo-person-or-purpose-v1"
            value={requestId}
            onChange={(event) => setRequestId(event.target.value)}
            aria-describedby="sales-demo-reference-help"
          />
          <span id="sales-demo-reference-help" style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
            Use the same non-secret reference to retrieve an existing result safely instead of creating a duplicate.
          </span>
        </label>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          data-testid="sales-demo-provision"
          disabled={!valid || busy}
          onClick={provision}
          style={{ justifySelf: 'start' }}
        >
          {busy ? 'Provisioning…' : 'Provision sales demo workspace'}
        </button>
        {error && (
          <div role="alert" data-testid="sales-demo-provisioning-error" style={{ color: 'var(--danger)' }}>
            {error}
          </div>
        )}
        {result && (
          <div className="panel" data-testid="sales-demo-provisioning-result" style={{ margin: 0 }}>
            <div className="panel-body" style={{ display: 'grid', gap: 4, fontSize: 12 }}>
              <strong>{result.duplicate ? 'Existing workspace reused' : 'Synthetic workspace created'}</strong>
              <span>Immutable tenant ID: <code>{result.tenant_id}</code></span>
              <span>Entry point: <code>/t/{result.slug}/sales-demo</code></span>
              <span>Fixture version: <code>{result.fixture_version}</code></span>
              <span>{result.invitation_pending ? 'Salesperson invitation pending' : 'Workspace owner assigned'}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
